import { flatten } from 'utils/flatten/flatten';
import messagesInCanadianFrench from 'lang/fr-CA.json';
import messagesInDutch from 'lang/nl-NL.json';
import messagesInEnglish from 'lang/en.json';
import messagesInFrench from 'lang/fr-FR.json';
import messagesInGerman from 'lang/de-DE.json';
import messagesInItalian from 'lang/it-IT.json';
import messagesInNorwegian from 'lang/nb-NO.json';
import messagesInPolish from 'lang/pl.json';
import messagesInPortuguese from 'lang/pt-PT.json';
import messagesInSpanish from 'lang/es-ES.json';
import messagesInUnitedKingdomEnglish from 'lang/en-GB.json';
import messagesInCanadianEnglish from 'lang/en-CA.json';
import messagesInIrishEnglish from 'lang/en-IE.json';
import messagesInAustralianEnglish from 'lang/en-AU.json';
import messagesInHungarian from 'lang/hu-HU.json';
import messagesInSlovak from 'lang/sk-SK.json';
import { useMemo } from 'react';

export const DEFAULT_LOCALE = 'en';

export const DEFAULT_COUNTRY = 'US';

/**
 * Regional ENGLISH variants, layered OVER `en` rather than replacing it.
 *
 * These are overlays, not catalogues: each carries ONLY the strings its region actually says
 * differently — "waiting list" for "waitlist", "Postcode" for "Zip/Postal code", "basket" for "cart".
 * Everything else falls through to `en`.
 *
 * Sparse on purpose. A full 319-key copy works today and rots silently: reword a string in `en` and the
 * copies keep the old wording forever. Key-parity CI cannot see it — the keys all still exist — and the
 * stale value DIFFERS from `en`, so it reads as a deliberate regional choice rather than a leftover. An
 * overlay cannot rot that way, because the strings it does not mention do not exist in it. Matches
 * service-requests and hqos, which layer their regional English the same way.
 */
const regionalOverlays = new Map<string, Record<string, unknown>>([
  ['en-gb', messagesInUnitedKingdomEnglish.serviceBooking],
  ['en-ie', messagesInIrishEnglish.serviceBooking],
  ['en-au', messagesInAustralianEnglish.serviceBooking],
  ['en-ca', messagesInCanadianEnglish.serviceBooking],
]);

/**
 * Keyed by BOTH the regional tag and the bare language.
 *
 * The bare key is what makes a language reachable at all. Browsers routinely send a primary tag with
 * no region — Chrome set to German reports `de` — and a customer may be configured for a region we
 * ship no file for (`de-AT`, `fr-BE`, `es-MX`, `nl-BE`). Without the bare entry both fall through to
 * English with the language's own catalogue sitting on disk unused: eight of the eleven languages
 * here were unreachable that way, every one of them translated.
 *
 * `fr` resolves to fr-FR and `pt` to pt-PT — the regional file stays the exact match, so `fr-CA` and
 * a future `pt-BR` are unaffected.
 */
const messagesMap = new Map<string, Record<string, unknown>>([
  ['en', messagesInEnglish.serviceBooking],
  ['es-es', messagesInSpanish.serviceBooking],
  ['es', messagesInSpanish.serviceBooking],
  ['fr-ca', messagesInCanadianFrench.serviceBooking],
  ['fr-fr', messagesInFrench.serviceBooking],
  ['fr', messagesInFrench.serviceBooking],
  ['de-de', messagesInGerman.serviceBooking],
  ['de', messagesInGerman.serviceBooking],
  ['nl-nl', messagesInDutch.serviceBooking],
  ['nl', messagesInDutch.serviceBooking],
  ['it-it', messagesInItalian.serviceBooking],
  ['it', messagesInItalian.serviceBooking],
  ['nb-no', messagesInNorwegian.serviceBooking],
  ['nb', messagesInNorwegian.serviceBooking],
  ['no', messagesInNorwegian.serviceBooking],
  ['pl', messagesInPolish.serviceBooking],
  ['hu-hu', messagesInHungarian.serviceBooking],
  ['hu', messagesInHungarian.serviceBooking],
  ['sk-sk', messagesInSlovak.serviceBooking],
  ['sk', messagesInSlovak.serviceBooking],
  ['pt-pt', messagesInPortuguese.serviceBooking],
  ['pt', messagesInPortuguese.serviceBooking],
]);

/**
 * A tag can name a real catalogue and still be unusable by Intl. `en-us@posix` resolves its language
 * fine, but `new Intl.NumberFormat('en-us@posix')` throws a RangeError — as do `''` and `C`. Returning
 * it would hand the provider a locale that renders every word correctly and then crashes on the first
 * price or date, which is worse than the English fallback it replaced.
 */
const isIntlSafe = (tag: string): boolean => {
  try {
    return Boolean(new Intl.NumberFormat(tag)) && Boolean(new Intl.DateTimeFormat(tag));
  } catch {
    return false;
  }
};

export const getAvailableLocale = (userLocale: string): [string, string] => {
  // Normalize case + separator before the lookup: map keys are lower-case hyphenated, but a host may pass
  // a canonical ('hu-HU', region-capitalised by Intl.getCanonicalLocales) or underscore ('hu_HU') tag.
  // Matching raw dropped every such tag to English (VIS-1695).
  const normalized = userLocale.toLowerCase().replace(/_/g, '-');

  // Return the NORMALIZED tag, not the caller's. It becomes the IntlProvider locale and therefore the
  // argument to every Intl formatter, and an underscored tag is not valid BCP-47 — `hu_HU` throws a
  // RangeError in Intl.NumberFormat and Intl.DateTimeFormat. Handing back the raw input would resolve
  // the catalogue correctly and then crash on the first formatted number or date, which is worse than
  // the English fallback it replaced. Intl is case-insensitive, so the lower-cased form is safe.
  if (messagesMap.has(normalized) || regionalOverlays.has(normalized)) {
    return [normalized, isIntlSafe(normalized) ? normalized : DEFAULT_LOCALE];
  }

  const [lang] = normalized.split('-');

  if (messagesMap.has(lang)) {
    // Keep the reader's region for formatting, but only if Intl can consume it; otherwise fall back
    // to the bare language, which always can.
    return [lang, isIntlSafe(normalized) ? normalized : lang];
  }

  return [DEFAULT_LOCALE, DEFAULT_LOCALE];
};

interface LocaleData {
  messages: Record<string, string>;
  locale: string;
}

export const useLocaleData = (userLocale: string): LocaleData => {
  return useMemo(() => {
    const [messagesKey, locale] = getAvailableLocale(userLocale);
    const overlay = regionalOverlays.get(messagesKey);

    // An overlay merges over `en`; a catalogue stands alone. Spreading `en` under every locale would
    // paper a missing translation over with English instead of surfacing it.
    const messages = overlay
      ? { ...flatten(messagesMap.get(DEFAULT_LOCALE)), ...flatten(overlay) }
      : flatten(messagesMap.get(messagesKey));

    return { locale, messages: messages as Record<string, string> };
  }, [userLocale]);
};
